import axios from 'axios';
import { AUTHKEY, REFRESHKEY } from '../utils/keys.js';
import { getGroupId, setGroupId } from '../Providers/LayoutProvider/group-context.js';
import { refreshtoken } from './api-config.js';
import { APIENDPOINTS } from './ApiEndPoints/api-endpoints.js';

let isRefreshing = false;
let subscribers = [];

function onRefreshed(token) {
	subscribers.forEach((callback) => callback(token));
	subscribers = [];
}

function addSubscriber(callback) {
	subscribers.push(callback);
}

const baseURL = ['development', 'staging', 'production'].includes(
	process.env.REACT_APP_ENV
)
	? `${window.location.origin}/api`
	: 'https://dev.esign-proteantech.in/api';

const axiosInstance = axios.create({ baseURL });

export default axiosInstance;

axiosInstance.interceptors.request.use((req) => {
	const token = window.localStorage.getItem(AUTHKEY);
	if (
		window.location.hostname === 'localhost'
		&& window.localStorage.getItem('esign_local')
	) {
		const baseUrl = req.baseURL;
		const actionUrl = req.url;
		let updatedBaseUrl
			= window.location.protocol + '//' + window.location.hostname + ':';

		const projectName = actionUrl.split('/')[1];
		switch (projectName) {
		case 'iam': {
			updatedBaseUrl += '5001/api';

			break;
		}

		case 'esign': {
			updatedBaseUrl += '5000/api';

			break;
		}

		case 'report': {
			updatedBaseUrl += '5004/api';

			break;
		}

		case 'wfms': {
			updatedBaseUrl += '5002/api';

			break;
		}

		default: {
			updatedBaseUrl = baseUrl;
		}
		}

		req.baseURL = updatedBaseUrl;
	}

	if (token) {
		req.headers.Authorization = `Bearer ${token}`;
	}

	const groupId = getGroupId();
	if (groupId) {
		req.headers['x-esignpro-group-id'] = groupId;
	}

	return req;
});

axiosInstance.interceptors.response.use(
	(response) => {
		const currentGroupId = getGroupId();
		const responseGroupId = response?.data?.eSignProRequestId;
		if (!currentGroupId && responseGroupId) {
			setGroupId(responseGroupId);
		}

		return response.data;
	},
	async (err) => {
		// Any status codes that falls outside the range of 2xx cause this function to trigger
		const status = err.response?.status || 500;
		const errorMessage = err.response.data.message === 'User is not active';
		const isRefreshApiCall
			= err.config?.url === APIENDPOINTS.LOGIN.refreshToken;
		const isLogoutApi = err.config?.url === APIENDPOINTS.LOGIN.logout;
		// We can handle global errors here
		switch (status) {
		// Authentication (token related issues)
		case 401: {
			const refreshToken = window.localStorage.getItem(REFRESHKEY);
			const originalRequest = err.config;

			// Set up retry count on the original request
			originalRequest._retryCount ||= 0;

			if (originalRequest._retryCount >= 5) {
				console.error('Refresh token retry limit reached');
				window.localStorage.removeItem(AUTHKEY);
				window.localStorage.removeItem(REFRESHKEY);
				window.location.href = '/login';
				throw err;
			}

			if (isLogoutApi || errorMessage || !refreshToken || isRefreshApiCall) {
				window.localStorage.removeItem(AUTHKEY);
				window.localStorage.removeItem(REFRESHKEY);
				window.location.href = '/login';
				throw err;
			}

			if (isRefreshing) {
				return new Promise((resolve) => {
					addSubscriber((newToken) => {
						originalRequest.headers.Authorization = `Bearer ${newToken}`;
						resolve(axiosInstance(originalRequest));
					});
				});
			}

			isRefreshing = true;

			try {
				const response = await refreshtoken({ refreshToken });

				if (response.status === 201) {
					const newAccessToken = response.token;
					const newRefreshToken = response.refreshToken;

					window.localStorage.setItem(AUTHKEY, newAccessToken);
					window.localStorage.setItem(REFRESHKEY, newRefreshToken);
					isRefreshing = false;

					onRefreshed(newAccessToken);

					// Retry the original request
					originalRequest._retryCount += 1;
					originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
					return axiosInstance(originalRequest);
				}

				throw new Error('Refresh failed');
			} catch (error) {
				isRefreshing = false;
				window.localStorage.removeItem(AUTHKEY);
				window.localStorage.removeItem(REFRESHKEY);
				window.location.href = '/login';
				throw error;
			}
		}

		// Forbidden (permission related issues)
		case 403: {
			console.error('403', err?.response?.data?.message);
			return err?.response?.data;
		}

		// Bad request
		case 400: {
			console.error('400', err?.response?.data?.message);
			return err?.response?.data;
		}

		// Not found
		case 404: {
			console.error('404', err?.response?.data?.message);
			return err?.response?.data;
		}

		// Conflict
		case 409: {
			console.error('409', err?.response?.data?.message);
			return err?.response?.data;
		}

		// Unprocessable
		case 422: {
			console.error('422', err?.response?.data?.message);
			return err?.response?.data;
		}

		// Generic api error (server related) unexpected
		default: {
			return err?.response?.data;
		}
		}
	}
);
